import React, { useEffect, useState } from "react";
import {
  Modal,
  Stack,
  Text,
  Checkbox,
  SegmentedControl,
  MultiSelect,
  Group,
  Button,
  Tooltip,
  Alert,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  TIER_SYNC_COPY,
  syncSubject,
  possessive,
} from "../../utils/staffTierSyncCopy";

export interface TierMemberOption {
  value: string; // staff id
  label: string;
}

export interface TierSyncResult {
  /** "all" targets every member of the tier; a list targets specific staff. */
  staffIds: string[] | "all";
  applyService: boolean;
  applyCommission: boolean;
}

interface TierSyncModalProps {
  opened: boolean;
  onClose: () => void;
  /** Destination tier name — used in the copy. */
  tierName: string;
  /** Offer the "overwrite service list" option (tier.has_service_defaults). */
  offerServiceReplace: boolean;
  /** Offer the "overwrite commission structure" option (tier.has_commission_defaults). */
  offerCommission: boolean;
  /** When provided, render an all/choose member selector (bulk "apply to
   * members"). Omit for a single pre-scoped target. */
  members?: TierMemberOption[];
  /** Name of the single pre-scoped target when `members` is omitted (the staff
   * member being added/moved). */
  targetName?: string;
  /** Start the offered options checked — true for a deliberate "apply to
   * members", false for the pre-assignment confirmation. */
  defaultChecked?: boolean;
  loading?: boolean;
  confirmLabel?: string;
  onConfirm: (result: TierSyncResult) => Promise<void> | void;
  /** Pre-assignment confirmations show this to let the user complete the
   * assignment without applying any defaults. Omit for "apply to members"
   * (no assignment to skip). */
  onSkip?: () => Promise<void> | void;
  skipLabel?: string;
}

const optionLabel = (text: string, tooltip: string): React.ReactNode => (
  <Text span size="sm">
    {text}{" "}
    <Tooltip label={tooltip} multiline w={300} withArrow position="top">
      <Text
        span
        c="dimmed"
        style={{
          cursor: "help",
          display: "inline-flex",
          verticalAlign: "text-bottom",
        }}
      >
        <IconInfoCircle size={16} />
      </Text>
    </Tooltip>
  </Text>
);

/**
 * The single, standardized "sync a tier's config down to staff" confirmation.
 * Offers the two overwrite options (service list / commission structure), each
 * shown only when the destination tier defines that default, gated behind a
 * required acknowledgement. Confirm is disabled until at least one option is
 * checked and the acknowledgement is ticked.
 */
const TierSyncModal: React.FC<TierSyncModalProps> = ({
  opened,
  onClose,
  tierName,
  offerServiceReplace,
  offerCommission,
  members,
  targetName,
  defaultChecked = false,
  loading = false,
  confirmLabel = "Confirm",
  onConfirm,
  onSkip,
  skipLabel = "Assign without applying",
}) => {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const isBulk = Array.isArray(members);

  const [mode, setMode] = useState<"all" | "choose">("all");
  const [selected, setSelected] = useState<string[]>([]);
  const [applyService, setApplyService] = useState(false);
  const [applyCommission, setApplyCommission] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset every open so a prior acknowledgement / selection never carries over.
  useEffect(() => {
    if (opened) {
      setMode("all");
      setSelected([]);
      setApplyService(defaultChecked && offerServiceReplace);
      setApplyCommission(defaultChecked && offerCommission);
      setAcknowledged(false);
    }
  }, [opened, defaultChecked, offerServiceReplace, offerCommission]);

  const anyOptionOffered = offerServiceReplace || offerCommission;
  const anyOptionChecked =
    (offerServiceReplace && applyService) ||
    (offerCommission && applyCommission);
  const bulkCount =
    mode === "all"
      ? (members as TierMemberOption[] | undefined)?.length || 0
      : selected.length;
  const hasTargets = !isBulk ? true : bulkCount > 0;
  const canConfirm = acknowledged && anyOptionChecked && hasTargets && !loading;

  const subject = isBulk
    ? syncSubject(bulkCount)
    : targetName || "this staff member";
  const whoPossessive = possessive(subject);

  const handleConfirm = async (): Promise<void> => {
    const staffIds: string[] | "all" = !isBulk
      ? "all"
      : mode === "all"
        ? "all"
        : selected;
    await onConfirm({
      staffIds,
      applyService: offerServiceReplace && applyService,
      applyCommission: offerCommission && applyCommission,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Apply ${tierName} defaults`}
      centered
      size="lg"
      fullScreen={isMobile}
    >
      <Stack>
        <Text size="sm">{TIER_SYNC_COPY.helpText(tierName, subject)}</Text>

        {!anyOptionOffered ? (
          <Alert color="gray" variant="light">
            {TIER_SYNC_COPY.noDefaults}
          </Alert>
        ) : (
          <>
            {isBulk && (
              <>
                <Text size="sm" fw={500}>
                  Apply to
                </Text>
                <SegmentedControl
                  value={mode}
                  onChange={(v) => setMode(v as "all" | "choose")}
                  data={[
                    {
                      label: `All members (${(members as TierMemberOption[]).length})`,
                      value: "all",
                    },
                    { label: "Choose members", value: "choose" },
                  ]}
                />
                {mode === "choose" && (
                  <MultiSelect
                    data={members as TierMemberOption[]}
                    value={selected}
                    onChange={setSelected}
                    placeholder="Select staff members"
                    searchable
                    nothingFoundMessage="No members in this tier"
                  />
                )}
              </>
            )}

            {offerServiceReplace && (
              <Checkbox
                checked={applyService}
                onChange={(e) => setApplyService(e.currentTarget.checked)}
                styles={{ body: { alignItems: "flex-start" } }}
                label={optionLabel(
                  TIER_SYNC_COPY.serviceOptionLabel(whoPossessive),
                  TIER_SYNC_COPY.serviceOptionTooltip,
                )}
              />
            )}
            {offerCommission && (
              <Checkbox
                checked={applyCommission}
                onChange={(e) => setApplyCommission(e.currentTarget.checked)}
                styles={{ body: { alignItems: "flex-start" } }}
                label={optionLabel(
                  TIER_SYNC_COPY.commissionOptionLabel(whoPossessive),
                  TIER_SYNC_COPY.commissionOptionTooltip,
                )}
              />
            )}

            <Checkbox
              mt="xs"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.currentTarget.checked)}
              styles={{ body: { alignItems: "flex-start" } }}
              label={
                <Text span size="sm">
                  {TIER_SYNC_COPY.acknowledgeLabel}{" "}
                  <Text span c="red">
                    *
                  </Text>
                </Text>
              }
            />
          </>
        )}

        <Group justify="space-between">
          <Button variant="default" onClick={onClose} disabled={loading}>
            Back
          </Button>
          {anyOptionOffered && (
            <Group gap="sm">
              {onSkip && (
                <Button
                  variant="default"
                  onClick={() => onSkip()}
                  disabled={loading}
                >
                  {skipLabel}
                </Button>
              )}
              <Button
                color="var(--brand-teal)"
                disabled={!canConfirm}
                loading={loading}
                onClick={handleConfirm}
              >
                {confirmLabel}
              </Button>
            </Group>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};

export default TierSyncModal;
